import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, MailCheck, PhoneCall, Voicemail, History } from "lucide-react";

const CH = {
  email: { label: "Email sent", icon: MailCheck, cls: "bg-sky-500/15 text-sky-300" },
  call: { label: "Phone call", icon: PhoneCall, cls: "bg-emerald-500/15 text-emerald-300" },
  voicemail: { label: "Voicemail left", icon: Voicemail, cls: "bg-amber-500/15 text-amber-300" },
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const when = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const Entry = ({ e, i }) => {
  const c = CH[e.channel] || { label: e.channel, icon: History, cls: "bg-secondary text-muted-foreground" };
  const Icon = c.icon;
  return (
    <li className="relative pl-11 pb-4 last:pb-0" data-testid={`history-entry-${i}`}>
      <span className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center ${c.cls}`}><Icon className="w-4 h-4" /></span>
      <div className="text-sm font-semibold">{c.label}{e.template ? <span className="font-normal text-muted-foreground"> · {e.template}</span> : null}</div>
      <div className="text-xs text-muted-foreground">{when(e.at)}{e.by ? ` · by ${e.by}` : ""}</div>
    </li>
  );
};

export const ContactHistoryDialog = ({ prospect, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!prospect) return;
    setData(null);
    api.get(`/prospects/${prospect.id}/history`).then(({ data: d }) => setData(d)).catch(() => setData({ entries: [], error: true }));
  }, [prospect]);

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="contact-history-dialog">
        <DialogHeader>
          <DialogTitle>Contact history</DialogTitle>
          <DialogDescription>{prospect?.name}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            {!data.error && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="history-summary">
                {Object.entries(CH).map(([k, c]) => (
                  <div key={k} className="rounded-lg bg-secondary/50 py-2">
                    <div className="text-lg font-bold" data-testid={`history-count-${k}`}>{data.counts?.[k] || 0}</div>
                    <div className="text-[11px] text-muted-foreground">{k === "email" ? "Emails" : k === "call" ? "Calls" : "Voicemails"}</div>
                  </div>
                ))}
              </div>
            )}
            {!data.error && (
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1" data-testid="history-meta">
                <span>Status: <b className="text-foreground">{data.status}</b></span>
                <span>Last contacted: <b className="text-foreground">{day(data.lastContacted)}</b></span>
                {data.followUpDue && <span>Follow up: <b className="text-foreground">{day(data.followUpDue)}</b></span>}
              </div>
            )}
            {data.error ? <p className="text-sm text-rose-400" data-testid="history-error">Couldn't load the history. Please try again.</p>
              : data.entries.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="history-empty">No emails, calls or voicemails logged yet. Use “Mark as emailed”, “Log call” or “Log voicemail” in Marketing Assets → Prospect Outreach.</p>
              ) : (
                <ol className="relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-border" data-testid="history-list">
                  {data.entries.map((e, i) => <Entry key={`${e.at}-${i}`} e={e} i={i} />)}
                </ol>
              )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
